import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import {
  getSocietyRoles,
  createSocietyRole,
  updateSocietyRole,
  deleteSocietyRole,
} from "../../../../redux/actions/SocietyPortal/RolePermissionAction/SocietyRoleAction";
import { selectedSocietyStorer } from "../../../../utils/webStorer";
import { SYSTEM_ROLE_CODES } from "../../../../consts/permissions";
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ROLES_PERMISSIONS } from "../../../../Routing/routes";
import RoleModal from "./RoleModal";

const emptyForm = { role_name: "", role_code: "", description: "", is_active: true };

const Roles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const societyId = selectedSocietyStorer.getSocietyId();
  const { rolesData, loading, createLoading, updateLoading, deleteLoading } = useSelector(
    (s) => s.SocietyRoleReducer
  );

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    if (societyId) dispatch(getSocietyRoles({ society_id: societyId }));
  }, [dispatch, societyId]);

  const refresh = () => dispatch(getSocietyRoles({ society_id: societyId }));
  usePageRefresh(refresh);

  const isSystemRole = (role) =>
    role?.is_system_role === 1 || SYSTEM_ROLE_CODES.includes(role?.role_code);

  const handleOpenAdd = () => {
    setFormData(emptyForm);
    setEditId(null);
    setShowModal(true);
  };

  const handleEdit = (role) => {
    if (isSystemRole(role)) return;
    setFormData({
      role_name: role.role_name || "",
      role_code: role.role_code || "",
      description: role.description || "",
      is_active: role.is_active === 1,
    });
    setEditId(role.role_id);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setFormData(emptyForm);
    setEditId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const onSuccess = () => {
      handleClose();
      refresh();
    };

    if (editId) {
      dispatch(
        updateSocietyRole({
          society_id: societyId,
          role_id: editId,
          role_name: formData.role_name,
          role_code: formData.role_code,
          description: formData.description || null,
          is_active: !!formData.is_active,
          onSuccess,
        })
      );
    } else {
      dispatch(
        createSocietyRole({
          society_id: societyId,
          role_name: formData.role_name,
          role_code: formData.role_code,
          description: formData.description || null,
          onSuccess,
        })
      );
    }
  };

  const handleDelete = (role) => {
    if (isSystemRole(role)) return;
    if (!window.confirm(`Delete role "${role.role_name}"? This cannot be undone.`)) return;
    dispatch(
      deleteSocietyRole({
        society_id: societyId,
        role_id: role.role_id,
        onSuccess: refresh,
      })
    );
  };

  const handleEditPermissions = (role) => {
    const path = SOCIETY_PORTAL + SOCIETY_PORTAL_ROLES_PERMISSIONS.replace(":roleId", role.role_id);
    navigate(path);
  };

  const roles = rolesData || [];

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h6 className="fw-semibold mb-0 text-xl">Roles & Permissions</h6>
          <p className="text-secondary-light text-sm mb-0 mt-1">
            Define custom roles and assign which permissions each role grants to its users.
          </p>
        </div>
        <button
          onClick={handleOpenAdd}
          className="btn d-flex align-items-center gap-2 px-20 py-12 radius-8 text-white"
          style={{ backgroundColor: "#f59e0b" }}
          disabled={loading}
        >
          <Icon icon="mdi:plus-circle" className="text-xl" />
          Add Custom Role
        </button>
      </div>

      <div className="card radius-8 border-0 mb-24 bg-primary-50">
        <div className="card-body p-16 d-flex align-items-center gap-3">
          <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
            <Icon icon="mdi:information-outline" className="text-primary-600 text-xl" />
          </div>
          <p className="text-sm text-secondary-light mb-0">
            <strong className="text-primary-600">Note:</strong> System roles (Society Admin,
            Society Manager, Resident) appear in this list and can be assigned to users. Their
            name and code cannot be changed, and they cannot be deleted, but their permissions
            can be edited by a super admin — changes apply globally across every society.
          </p>
        </div>
      </div>

      {loading && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {!loading && (
        <div className="card radius-12 border-0 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="ps-20 py-16 text-secondary-light text-sm fw-semibold">#</th>
                    <th className="py-16 text-secondary-light text-sm fw-semibold">Role Name</th>
                    <th className="py-16 text-secondary-light text-sm fw-semibold">Code</th>
                    <th className="py-16 text-secondary-light text-sm fw-semibold">Description</th>
                    <th className="py-16 text-secondary-light text-sm fw-semibold">Type</th>
                    <th className="py-16 text-secondary-light text-sm fw-semibold">Permissions</th>
                    <th className="py-16 text-secondary-light text-sm fw-semibold">Status</th>
                    <th className="pe-20 py-16 text-secondary-light text-sm fw-semibold text-end">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role, index) => {
                    const sysRole = isSystemRole(role);
                    return (
                      <tr key={role.role_id}>
                        <td className="ps-20 py-16 align-middle">
                          <span className="text-secondary-light fw-medium">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        </td>
                        <td className="py-16 align-middle">
                          <span className="fw-medium">{role.role_name}</span>
                        </td>
                        <td className="py-16 align-middle">
                          <span className="badge bg-neutral-100 text-secondary-light fw-medium px-12 py-6 radius-4">
                            {role.role_code}
                          </span>
                        </td>
                        <td className="py-16 align-middle text-secondary-light text-sm">
                          {role.description || "—"}
                        </td>
                        <td className="py-16 align-middle">
                          <span
                            className={`badge px-12 py-6 radius-4 fw-medium ${
                              sysRole
                                ? "bg-info-50 text-info-600"
                                : "bg-success-50 text-success-600"
                            }`}
                          >
                            {sysRole ? "System" : "Custom"}
                          </span>
                        </td>
                        <td className="py-16 align-middle">
                          <span className="text-secondary-light fw-medium">
                            {role.permission_count ?? 0}
                          </span>
                        </td>
                        <td className="py-16 align-middle">
                          <span
                            className={`badge px-12 py-6 radius-4 fw-medium ${
                              role.is_active === 1
                                ? "bg-success-50 text-success-600"
                                : "bg-neutral-100 text-secondary-light"
                            }`}
                          >
                            {role.is_active === 1 ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="pe-20 py-16 align-middle text-end">
                          <div className="d-flex align-items-center justify-content-end gap-2">
                            <button
                              onClick={() => handleEditPermissions(role)}
                              className="btn btn-sm btn-outline-info d-flex align-items-center gap-1 px-12 py-6 radius-6"
                              title="View / edit permissions"
                            >
                              <Icon icon="mdi:shield-key-outline" className="text-lg" />
                              <span className="d-none d-lg-inline">Permissions</span>
                            </button>
                            <button
                              onClick={() => handleEdit(role)}
                              className="btn btn-sm btn-primary d-flex align-items-center gap-1 px-12 py-6 radius-6"
                              disabled={sysRole}
                              title={sysRole ? "System roles can't be edited" : "Edit role"}
                            >
                              <Icon icon="mdi:pencil" className="text-lg" />
                              <span className="d-none d-lg-inline">Edit</span>
                            </button>
                            <button
                              onClick={() => handleDelete(role)}
                              className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-12 py-6 radius-6"
                              disabled={sysRole || deleteLoading}
                              title={sysRole ? "System roles can't be deleted" : "Delete role"}
                            >
                              <Icon icon="mdi:trash-can-outline" className="text-lg" />
                              <span className="d-none d-lg-inline">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {roles.length === 0 && (
              <div className="p-40 text-center">
                <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                  <Icon icon="mdi:shield-account-outline" className="text-secondary-light text-4xl" />
                </div>
                <h6 className="fw-semibold mb-2">No Roles Yet</h6>
                <p className="text-secondary-light text-sm mb-20">
                  Add a custom role to start tailoring permissions for this society.
                </p>
                <button
                  onClick={handleOpenAdd}
                  className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                >
                  <Icon icon="mdi:plus-circle" className="text-xl" />
                  Add First Role
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <RoleModal
        show={showModal}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        editId={editId}
        loading={createLoading || updateLoading}
      />
    </>
  );
};

export default Roles;
